import { useState, useRef, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send, Newspaper, Plane, Cloud, Calendar, Globe, Sun, Wind, Umbrella, Building } from "lucide-react";
import { askGroq } from "@/lib/groq";
import { planTrip } from "@/lib/agent";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ReactMarkdown from "react-markdown";
import NewsList from "@/components/news/NewsList";
import { Compass, MapPin } from "lucide-react";

interface Message {
  role: "user" | "assistant";
  content: string;
  travelPlan?: any;
  needsUserInput?: {
    origin?: boolean;
    date?: boolean;
  };
}

interface PartialTravelInfo {
  destination?: string;
  origin?: string;
  date?: string;
}

const ChatInterface = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Hello! I'm your AI travel assistant. How can I help plan your trip today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [travelPlan, setTravelPlan] = useState<any>(null);
  const [partialTravelInfo, setPartialTravelInfo] = useState<PartialTravelInfo>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateDestinationInfo = async (destination: string) => {
    const prompt = `Generate a brief, engaging 2-3 sentence overview of ${destination} as a travel destination, 
    highlighting key attractions and best time to visit. Keep it concise but informative.`;
    
    try {
      const response = await askGroq([
        { role: "system", content: prompt },
        { role: "user", content: destination }
      ]);
      return response;
    } catch (error) {
      console.error("Error generating destination info:", error);
      return `${destination} is a fascinating destination with lots to explore! Let me help you plan your trip there.`;
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput("");
    
    // Add user message to chat
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    
    setIsLoading(true);

    // Enhanced retry logic with exponential backoff
    const retryWithExponentialBackoff = async (
      fn: () => Promise<any>,
      maxRetries = 3,
      initialDelay = 5000
    ) => {
      let retries = 0;
      
      while (retries < maxRetries) {
        try {
          return await fn();
        } catch (error: any) {
          if (
            error.message &&
            (error.message.includes('rate_limit_exceeded') || error.message.includes('429'))
          ) {
            retries++;
            if (retries === maxRetries) throw error;

            const delay = initialDelay * Math.pow(2, retries - 1);
            const remainingAttempts = maxRetries - retries;
            
            setMessages(prev => [...prev, {
              role: "assistant",
              content: `I'm experiencing high traffic at the moment. I'll try again in ${delay/1000} seconds. ${remainingAttempts} ${remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining...`
            }]);
            
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            throw error;
          }
        }
      }
    };

    // Function to generate travel plan in chunks
    const generateTravelPlan = async (query: string) => {
      try {
        // Convert messages to the format expected by planTrip
        const conversationHistory = messages.map(msg => ({
          role: msg.role,
          content: msg.content
        }));

        // First, try to get flight information
        const flightInfo = await retryWithExponentialBackoff(async () => {
          const response = await planTrip(query, conversationHistory);
          return response?.flights;
        });

        // Then, get weather information
        const weatherInfo = await retryWithExponentialBackoff(async () => {
          const response = await planTrip(query, conversationHistory);
          return response?.weather;
        });

        // Finally, get itinerary and news
        const mainInfo = await retryWithExponentialBackoff(async () => {
          const response = await planTrip(query, conversationHistory);
          return {
            itinerary: response?.itinerary,
            news: response?.news
          };
        });

        // Combine all information
        return {
          flights: flightInfo,
          weather: weatherInfo,
          ...mainInfo
        };
      } catch (error: any) {
        console.error('Error generating travel plan:', error);
        throw error;
      }
    };
    
    try {
      // Check if this is a response to a previous question about missing details
      const lastMessage = messages[messages.length - 1];
      
      if (lastMessage.needsUserInput) {
        if (lastMessage.needsUserInput.origin) {
          // User provided origin city
          const origin = userMessage;
          setPartialTravelInfo(prev => ({ ...prev, origin }));
          
          // Ask for travel date
          setMessages(prev => [...prev, {
            role: "assistant",
            content: `Great! When would you like to travel to ${partialTravelInfo.destination} from ${origin}? Please specify a date (e.g., July 13, 2025).`,
            needsUserInput: { date: true }
          }]);
          setIsLoading(false);
          return;
        } 
        else if (lastMessage.needsUserInput.date) {
          // User provided travel date
          const date = userMessage;
          const updatedTravelInfo = { ...partialTravelInfo, date };
          setPartialTravelInfo(updatedTravelInfo);
          
          try {
            // Construct a properly formatted query string
            const formattedDate = date.toLowerCase().includes('20') ? date : `${date} in 2025`;
            const fullQuery = `Plan a trip to ${updatedTravelInfo.destination} from ${updatedTravelInfo.origin} on ${formattedDate}`;
            
            setMessages(prev => [...prev, {
              role: "assistant",
              content: "Great! Let me create a detailed travel plan for you. This might take a moment as I gather all the information...",
            }]);

            // Generate travel plan in chunks
            const travelPlan = await generateTravelPlan(fullQuery);

            if (!travelPlan || !travelPlan.itinerary) {
              throw new Error("Incomplete travel plan generated");
            }

            setMessages(prev => [...prev, {
              role: "assistant",
              content: "I've created a detailed travel plan for you. Here's what I recommend:",
              travelPlan
            }]);
          } catch (error: any) {
            console.error("Error generating travel plan:", error);
            
            if (error.message && error.message.includes('rate_limit_exceeded')) {
              setMessages(prev => [...prev, {
                role: "assistant",
                content: "I apologize, but I'm experiencing high traffic right now. Please try again in a few moments by typing:\n\n" +
                        `'Plan a trip to ${updatedTravelInfo.destination} from ${updatedTravelInfo.origin} on ${updatedTravelInfo.date}'`
              }]);
            } else {
              setMessages(prev => [...prev, {
                role: "assistant",
                content: "I apologize, but I encountered an error. Please try again by typing:\n\n" +
                        `'Plan a trip to ${updatedTravelInfo.destination} from ${updatedTravelInfo.origin} on ${updatedTravelInfo.date}'`
              }]);
            }
          }
          setIsLoading(false);
          return;
        }
      }

      // Handle new travel query
      const travelRegex = /(?:travel|trip|go|plan)\s+(?:a\s+)?(?:trip\s+)?to\s+(\w+)/i;
      const match = userMessage.match(travelRegex);
      
      if (match) {
        const destination = match[1].charAt(0).toUpperCase() + match[1].slice(1);
        
        // Check if the message contains complete travel details
        const hasOrigin = userMessage.toLowerCase().includes(" from ");
        const hasDate = userMessage.toLowerCase().includes(" on ") || userMessage.toLowerCase().includes(" in 202");
        
        if (hasOrigin && hasDate) {
          // Complete travel query - proceed with full plan
          try {
            setMessages(prev => [...prev, {
              role: "assistant",
              content: "Great! Let me create a detailed travel plan for you with flights, weather, and local news...",
            }]);

            const travelPlan = await planTrip(userMessage);
            
            if (!travelPlan) {
              throw new Error("Failed to generate travel plan");
            }

            setMessages(prev => [...prev, {
              role: "assistant",
              content: "I've created a detailed travel plan for you. Here's what I recommend:",
              travelPlan
            }]);
          } catch (error: any) {
            console.error("Error generating travel plan:", error);
            setMessages(prev => [...prev, {
              role: "assistant",
              content: "I apologize, but I encountered an error. Please try typing your request in this format:\n\n" +
                      "'Plan a trip to [destination] from [origin] on [date]'\n\n" +
                      "For example: 'Plan a trip to Hyderabad from Goa on July 13, 2025'"
            }]);
          }
        } else {
          // Incomplete travel query - start collecting details
          setPartialTravelInfo({ destination });
          
          try {
            // Generate destination information
            const destinationInfo = await generateDestinationInfo(destination);
            
            // Send response with destination info and ask for origin
            setMessages(prev => [...prev, {
              role: "assistant",
              content: `${destinationInfo}\n\nTo help you plan your trip better, where will you be traveling from?`,
              needsUserInput: { origin: true }
            }]);
          } catch (error) {
            console.error("Error generating destination info:", error);
            setMessages(prev => [...prev, {
              role: "assistant",
              content: `${destination} is a great choice! To help you plan your trip better, where will you be traveling from?`,
              needsUserInput: { origin: true }
            }]);
          }
        }
      } else {
        // Regular conversation - get response from AI
        try {
          const response = await askGroq([
            { role: "system", content: "You are a helpful travel assistant. Provide concise, informative responses." },
            { role: "user", content: userMessage }
          ]);
          
          setMessages(prev => [...prev, {
            role: "assistant",
            content: response
          }]);
        } catch (error) {
          console.error("Error getting AI response:", error);
          setMessages(prev => [...prev, {
            role: "assistant",
            content: "I apologize, but I encountered an error. Please try again or rephrase your question."
          }]);
        }
      }
    } catch (error: any) {
      console.error("Error in chat:", error);
      
      // Check if it's a rate limit error
      if (error.message && error.message.includes('rate_limit_exceeded')) {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "I'm experiencing high traffic at the moment. Please try again in a few seconds with your complete travel query."
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "I apologize, but I encountered an error. Please try again with a complete travel query in this format:\n\n" +
                  "'Plan a trip to [destination] from [origin] on [date]'"
        }]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const extractDestination = (message: string): string => {
    // Simple extraction - could be made more sophisticated
    const words = message.toLowerCase().split(' ');
    const toIndex = words.indexOf('to');
    if (toIndex !== -1 && words[toIndex + 1]) {
      return words[toIndex + 1].charAt(0).toUpperCase() + words[toIndex + 1].slice(1);
    }
    // Fallback to last word if "to" not found
    return words[words.length - 1].charAt(0).toUpperCase() + words[words.length - 1].slice(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderTravelPlan = (travelPlan: any) => {
    return (
      <div className="mt-4 bg-gray-50 rounded-lg overflow-hidden">
        <Tabs defaultValue="itinerary" className="w-full">
          <TabsList className="flex w-full bg-white border-b">
            <TabsTrigger 
              value="itinerary" 
              className="flex-1 px-6 py-3 data-[state=active]:text-travel-blue data-[state=active]:border-b-2 data-[state=active]:border-travel-blue"
            >
              <Calendar className="w-4 h-4 mr-2" />
              <span>Itinerary</span>
            </TabsTrigger>
            <TabsTrigger 
              value="flights" 
              className="flex-1 px-6 py-3 data-[state=active]:text-travel-blue data-[state=active]:border-b-2 data-[state=active]:border-travel-blue"
            >
              <Plane className="w-4 h-4 mr-2" />
              <span>Flights</span>
            </TabsTrigger>
            <TabsTrigger 
              value="weather" 
              className="flex-1 px-6 py-3 data-[state=active]:text-travel-blue data-[state=active]:border-b-2 data-[state=active]:border-travel-blue"
            >
              <Cloud className="w-4 h-4 mr-2" />
              <span>Weather</span>
            </TabsTrigger>
            <TabsTrigger 
              value="news" 
              className="flex-1 px-6 py-3 data-[state=active]:text-travel-blue data-[state=active]:border-b-2 data-[state=active]:border-travel-blue"
            >
              <Newspaper className="w-4 h-4 mr-2" />
              <span>News</span>
            </TabsTrigger>
          </TabsList>

          {/* Itinerary Content */}
          <TabsContent value="itinerary" className="p-6">
            <div className="prose max-w-none">
              <ReactMarkdown>{travelPlan.itinerary}</ReactMarkdown>
            </div>
          </TabsContent>

          {/* Flights Content */}
          <TabsContent value="flights" className="p-6">
            {travelPlan.flights?.data?.length > 0 ? (
              <div className="space-y-4">
                {travelPlan.flights.data.map((flight: any, index: number) => (
                  <div key={index} className="bg-white rounded-lg border p-4 hover:border-travel-blue transition-colors">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                          <Plane className="w-6 h-6 text-travel-blue" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <p className="font-medium text-lg">{flight.itineraries[0].segments[0].carrierCode}</p>
                            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              Flight {flight.itineraries[0].segments[0].number}
                            </span>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-500">Departure</p>
                              <p className="font-medium">{new Date(flight.itineraries[0].segments[0].departure.at).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Arrival</p>
                              <p className="font-medium">{new Date(flight.itineraries[0].segments[0].arrival.at).toLocaleString()}</p>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mt-2">
                            Duration: <span className="font-medium">{flight.itineraries[0].segments[0].duration}</span>
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-travel-blue">₹{parseFloat(flight.price.total).toLocaleString()}</p>
                        <p className="text-sm text-gray-500">per person</p>
                        <Button variant="outline" className="mt-2 border-travel-blue text-travel-blue hover:bg-travel-blue hover:text-white">
                          Select Flight
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-2">No flight options available for this route.</p>
                <p className="text-sm text-gray-400">Try searching for a different date or route</p>
              </div>
            )}
          </TabsContent>

          {/* Weather Content */}
          <TabsContent value="weather" className="p-6">
            {travelPlan.weather ? (
              <div className="bg-white rounded-lg border p-6">
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-travel-blue mb-2">
                    {Math.round(travelPlan.weather.main.temp)}°C
                  </div>
                  <p className="text-xl capitalize text-gray-700">{travelPlan.weather.weather[0].description}</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Humidity</p>
                    <p className="text-xl font-semibold text-travel-blue">{travelPlan.weather.main.humidity}%</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Wind Speed</p>
                    <p className="text-xl font-semibold text-travel-blue">{travelPlan.weather.wind.speed} m/s</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Pressure</p>
                    <p className="text-xl font-semibold text-travel-blue">{travelPlan.weather.main.pressure} hPa</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Visibility</p>
                    <p className="text-xl font-semibold text-travel-blue">{(travelPlan.weather.visibility / 1000).toFixed(1)} km</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">Weather information not available for this location.</p>
              </div>
            )}
          </TabsContent>

          {/* News Content */}
          <TabsContent value="news" className="p-6">
            {travelPlan.news && travelPlan.news.length > 0 ? (
              <div className="space-y-6">
                {travelPlan.news.map((article: any, index: number) => (
                  <div key={index} className="bg-white rounded-lg border p-6 hover:border-travel-blue transition-colors">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg bg-travel-blue/10 flex items-center justify-center">
                          <Newspaper className="w-6 h-6 text-travel-blue" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold mb-2 text-gray-900">{article.title}</h3>
                        <p className="text-gray-600 mb-4 line-clamp-3">{article.description || article.content || "No summary available."}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-500">
                              {new Date(article.publishedAt).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                            {article.source?.name && (
                              <span className="text-sm px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                                {article.source.name}
                              </span>
                            )}
                          </div>
                          <a 
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 text-travel-blue hover:underline font-medium"
                          >
                            <span>Read Full Article</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Newspaper className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 text-lg mb-2">No recent news available</p>
                <p className="text-gray-400">We couldn't find any news articles for this destination at the moment.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Chat Container */}
        <div className="flex flex-col h-[calc(100vh-4rem)]">
          {/* Chat Messages Area */}
          <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
            <div className="space-y-8">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${
                    message.role === "assistant" ? "items-start" : "items-start justify-end"
                  }`}
                >
                  {message.role === "assistant" && (
                    <div className="w-12 h-12 rounded-full bg-travel-blue flex items-center justify-center text-white mr-4 flex-shrink-0">
                      <Globe className="w-6 h-6" />
                    </div>
                  )}
                  <div
                    className={`${
                      message.role === "assistant"
                        ? "bg-white border border-gray-200"
                        : "bg-travel-blue text-white"
                    } p-6 rounded-lg shadow-sm ${message.travelPlan ? "max-w-[95%] w-full" : "max-w-[80%]"}`}
                  >
                    <p className="text-base leading-relaxed mb-4">{message.content}</p>
                    {message.travelPlan && renderTravelPlan(message.travelPlan)}
                  </div>
                  {message.role === "user" && (
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center ml-4 flex-shrink-0">
                      <div className="w-6 h-6 rounded-full bg-travel-blue" />
                    </div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="border-t bg-white p-4 md:p-6">
            <div className="max-w-5xl mx-auto flex items-center space-x-4">
              <div className="flex-1 relative">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about destinations, flights, or say 'Plan a trip to...' to get started"
                  className="resize-none w-full border-2 rounded-xl px-4 py-3 focus:border-travel-blue focus:ring-0 text-base"
                  rows={1}
                  disabled={isLoading}
                />
              </div>
              <Button
                className={`rounded-xl p-4 h-14 w-14 flex items-center justify-center ${
                  isLoading || !input.trim()
                    ? 'bg-gray-100 text-gray-400'
                    : 'bg-travel-blue hover:bg-blue-700 text-white'
                }`}
                onClick={handleSendMessage}
                disabled={isLoading || !input.trim()}
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-6 h-6" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
